import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { useEffect, useMemo, useState } from 'react';
import { getClosedAnomalyColumnDefs } from './TableContent';
import { useNavigate } from 'react-router-dom';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDynamicFilters, getAnomalyTickets } from '../../actions/AnomalyTrackerAction';
import { RootState } from '@cp/src/store';
import {
  AnomalyStatus,
  AnomalyStatusType,
  AnomalyTableKeys,
  IAnomalyData,
  SortOrder
} from '../../types';
import { DEFAULT_PAGINATION_STATE } from '../../constants';
import { setSingleAnomalyDetailsLoading } from '../../reducers/AnomalyTrackerSlice';
import Modal from '@cp/src/components/Modal/Modal';
import AnomalyModalHeader from '../../components/AnomalyDetails/AnomalyModalHeader';
import AnomalyDetails from '../../components/AnomalyDetails';
import { getAnomalyActionUrl } from '../../utils';
import NoResultsFound from '../../components/NoResultsFound';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

const ClosedAnomaly = () => {
  const queryParams = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const closedAnomalyTickets = useSelector(
    (state: RootState) => state?.anomalyTracker?.closedTickets
  );
  const isLoading = useSelector((state: RootState) => state?.anomalyTracker?.loading);

  const params = queryParams[AnomalyTableKeys.CLOSED_ANOMALY_TABLE];
  const actionParams = queryParams[AnomalyTableKeys.ANOMALY_TABLE_ACTION];

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const defaultParams = {
      ...params,
      pageSize: params?.pageSize || 10,
      pageNo: params?.pageIndex || 0
    };
    delete defaultParams.pageIndex;
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_ANOMALY_TRACKER_PAGE_LOAD, {
      tabName: AnomalyStatus.CLOSED
    });
    dispatch(getAnomalyTickets(AnomalyStatus.CLOSED, defaultParams));
    dispatch(fetchDynamicFilters(AnomalyStatusType.RESOLVED));
  }, []);

  useEffect(() => {
    if (actionParams?.selectedId) {
      setIsModalOpen(true);
      dispatch(setSingleAnomalyDetailsLoading(true));
    }
  }, [actionParams?.selectedId]);

  const viewAnomalyHandler = (anomalyData: IAnomalyData) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_ANOMALY_TRACKER_ANOMALY_CLICK, {
      anomalyId: anomalyData?.anomalyId,
      tabName: AnomalyStatus.CLOSED
    });
    const url = getAnomalyActionUrl(anomalyData?.anomalyReferenceId?.toString());
    navigate(url);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    const updatedTableParams = { ...queryParams };
    delete updatedTableParams[AnomalyTableKeys.ANOMALY_TABLE_ACTION];
    const url = createQueryParams(updatedTableParams);
    navigate(url);
  };

  const onPaginationChange = (pagination: { pageSize: number; pageIndex: number }) => {
    const defaultParams = {
      ...params,
      pageSize: pagination?.pageSize || 10,
      pageNo: pagination?.pageIndex || 0
    };
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_ANOMALY_TRACKER_PAGINATION_CHANGE, {
      pageSize: pagination?.pageSize || 10,
      pageNo: pagination?.pageIndex || 0,
      tabName: AnomalyStatus.CLOSED
    });
    delete defaultParams.pageIndex;
    dispatch(getAnomalyTickets(AnomalyStatus.CLOSED, defaultParams));
  };

  const onSortingChange = (sorting: { id: string; desc: boolean }) => {
    const defaultParams = {
      ...params,
      sortBy: sorting?.id,
      sortDirection: sorting?.desc ? SortOrder.DESC : SortOrder.ASC,
      pageNo: 0
    };
    delete defaultParams.pageIndex;
    dispatch(getAnomalyTickets(AnomalyStatus.CLOSED, defaultParams));
  };

  const CLOSED_ANOMALY_COLUMN_DEFS_NAVI_TABLE = useMemo(
    () => getClosedAnomalyColumnDefs(viewAnomalyHandler),
    []
  );

  return (
    <div className="p-6 h-[calc(100vh-148px)]">
      <NaviTable
        tableName={AnomalyTableKeys.CLOSED_ANOMALY_TABLE}
        columns={CLOSED_ANOMALY_COLUMN_DEFS_NAVI_TABLE}
        data={closedAnomalyTickets?.data || []}
        totalElements={closedAnomalyTickets?.pages?.totalElements}
        loading={isLoading}
        enableRowClick={() => true}
        onRowClick={viewAnomalyHandler}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        defaultPaginationState={DEFAULT_PAGINATION_STATE}
        emptyDataComponent={<NoResultsFound />}
      />
      <Modal
        key="closedAnomalyView"
        onClose={onModalClose}
        visible={isModalOpen}
        contentClassName="!p-0"
        closeOnOutsideClick
        headerClassName="w-[944px] bg-[var(--navi-color-navigation-blue-c1)] !items-start rounded-lg !pr-4"
        header={<AnomalyModalHeader />}
      >
        <AnomalyDetails />
      </Modal>
    </div>
  );
};

export default ClosedAnomaly;
